import { IdMap, Scope, ViewMode } from './types';
import {
  ConfigurationRuleScopeEntityType,
  ConfigurationRuleScopeMode,
  ConfigurationRuleSetupType,
} from '@ydistri/api-sdk';
import cloneDeep from 'lodash.clonedeep';

export const numberArrayToObject = (list: number[]): IdMap => {
  return list.reduce<IdMap>((p, c) => {
    p[c] = true;
    return p;
  }, {});
};

export const MAIN_SCOPE_PRIORITY = 0;

/**
 * Check if the priority corresponds to the main scope
 * @param priority priority to test
 * @returns true if the priority corresponds to the main scope (0), false otherwise
 */
export const isMainScopePriority = (priority: number): boolean => {
  return priority === MAIN_SCOPE_PRIORITY;
};

/**
 * Check if the scope is the main scope
 * @param scope scope to test
 * @returns true if the scope is the main scope (priority 0), false otherwise
 */
export const isMainScope = (scope: Scope | undefined): boolean => {
  return (scope && isMainScopePriority(scope.priority)) ?? false;
};

export const emptyScope: Scope = {
  id: -1, //empty scope id
  name: undefined,
  priority: MAIN_SCOPE_PRIORITY,
  creating: true,
  entities: {
    [ConfigurationRuleScopeEntityType.Regions]: {
      selectionOption: ConfigurationRuleScopeMode.All,
      selectedIds: [],
    },
    [ConfigurationRuleScopeEntityType.Stores]: {
      selectionOption: ConfigurationRuleScopeMode.All,
      selectedIds: [],
    },
    [ConfigurationRuleScopeEntityType.StoreTypes]: {
      selectionOption: ConfigurationRuleScopeMode.All,
      selectedIds: [],
    },
    [ConfigurationRuleScopeEntityType.Departments]: {
      selectionOption: ConfigurationRuleScopeMode.All,
      selectedIds: [],
    },
  },
  configuration: {
    [ConfigurationRuleSetupType.UseAsSource]: true,
    [ConfigurationRuleSetupType.UseAsTarget]: true,
    [ConfigurationRuleSetupType.IsEnabled]: true,
    [ConfigurationRuleSetupType.MaxNumberOfSourceStores]: 0,
    [ConfigurationRuleSetupType.MaxNumberOfTargetStores]: 0,
    [ConfigurationRuleSetupType.MaxNumberOfSourcePickingPositions]: 0,
    [ConfigurationRuleSetupType.MinimumRouteValue]: 0,
  },
};

export const createEmptyScope = (
  id: number,
  name: string | undefined,
  priority: number,
  creating: boolean = true,
): Scope => ({
  ...cloneDeep(emptyScope),
  id,
  name,
  priority,
  creating,
  configuration: isMainScopePriority(priority)
    ? { ...emptyScope.configuration }
    : {
        [ConfigurationRuleSetupType.UseAsSource]: null,
        [ConfigurationRuleSetupType.UseAsTarget]: null,
        [ConfigurationRuleSetupType.IsEnabled]: null,
        [ConfigurationRuleSetupType.MaxNumberOfSourceStores]: null,
        [ConfigurationRuleSetupType.MaxNumberOfTargetStores]: null,
        [ConfigurationRuleSetupType.MaxNumberOfSourcePickingPositions]: null,
        [ConfigurationRuleSetupType.MinimumRouteValue]: null,
      },
});

const MODAL_VIEW_MODES = new Set([ViewMode.ADJUST_SCOPE_MODAL, ViewMode.NEW_EXCEPTION_MODAL]);
const NON_MODAL_VIEW_MODES = new Set([ViewMode.EXCEPTION, ViewMode.GENERAL_CONFIGURATION]);

export const isModalViewMode = (viewMode: ViewMode): boolean => MODAL_VIEW_MODES.has(viewMode);
export const isNonModalViewMode = (viewMode: ViewMode): boolean =>
  NON_MODAL_VIEW_MODES.has(viewMode);
